import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { tap, catchError, filter, take } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../environments/environment';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

interface JwtPayload {
  sub: string;
  roles: string[];
  exp: number;
}

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const API_BASE = environment.apiUrl;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // ─── Token storage ────────────────────────────────────────────────────────

  getAccessToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  }

  private saveTokens(tokens: TokenResponse): void {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  }

  clearTokens(): void {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  // ─── JWT helpers ─────────────────────────────────────────────────────────

  private decodeToken(token: string): JwtPayload | null {
    try {
      return jwtDecode<JwtPayload>(token);
    } catch {
      return null;
    }
  }

  getRoles(): string[] {
    const token = this.getAccessToken();
    if (!token) return [];
    return this.decodeToken(token)?.roles ?? [];
  }

  /**
   * Gate for this panel specifically. Deliberately PLATFORM_ADMIN, not ADMIN - ADMIN just
   * means "owns one company" in PlankERP's role model (the same role used across every
   * tenant's own admin account), while this panel operates across every tenant. The backend
   * enforces the real boundary (AdminOperationsController requires PLATFORM_ADMIN too); this
   * check only keeps a company owner from getting as far as a broken, all-403 dashboard.
   */
  isPlatformAdmin(): boolean {
    return this.getRoles().includes('PLATFORM_ADMIN');
  }

  isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }

  // ─── API calls ────────────────────────────────────────────────────────────

  login(credentials: LoginRequest): Observable<TokenResponse> {
    if (environment.mockApi) {
      const demoToken = this.createDemoToken(credentials.email || 'admin@plankerp.local');
      const tokens = { accessToken: demoToken, refreshToken: demoToken };
      this.saveTokens(tokens);
      return of(tokens);
    }
    return this.http.post<TokenResponse>(`${API_BASE}/auth`, credentials).pipe(
      tap(tokens => this.saveTokens(tokens))
    );
  }

  logoutRemote(): Observable<void> {
    if (environment.mockApi) return of(void 0);
    const token = this.getAccessToken();
    return this.http.post<void>(`${API_BASE}/auth/logout`, { token });
  }

  logout(): void {
    this.logoutRemote().subscribe({ error: () => {} }).add(() => {
      this.clearTokens();
      this.router.navigate(['/login']);
    });
  }

  checkToken(): Observable<void> {
    if (environment.mockApi) return of(void 0);
    const token = this.getAccessToken();
    return this.http.post<void>(`${API_BASE}/auth/check-token`, { token });
  }

  refreshAccessToken(): Observable<TokenResponse> {
    if (environment.mockApi) {
      const token = this.getAccessToken() ?? this.createDemoToken('admin@plankerp.local');
      const tokens = { accessToken: token, refreshToken: token };
      this.saveTokens(tokens);
      return of(tokens);
    }
    const token = this.getRefreshToken();
    return this.http
      .post<TokenResponse>(`${API_BASE}/auth/refresh`, { token })
      .pipe(
        tap(tokens => this.saveTokens(tokens))
      );
  }

  // ─── Refresh queue (używane przez interceptor) ────────────────────────────

  get isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }

  startRefresh(): Observable<TokenResponse> {
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.refreshAccessToken().pipe(
      tap(tokens => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(tokens.accessToken);
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.clearTokens();
        this.router.navigate(['/login']);
        return throwError(() => err);
      })
    );
  }

  waitForToken(): Observable<string | null> {
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1)
    );
  }

  private createDemoToken(email: string): string {
    const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
    const payload = btoa(JSON.stringify({
      sub: email,
      roles: ['PLATFORM_ADMIN'],
      exp: Math.floor(Date.now() / 1000) + 86400
    }));
    return `${header}.${payload}.local-demo`;
  }
}
